import type { HtmlAssetDescription } from '../types';

/** The generated-graphic-specific fields of a serialized asset description. */
export type HtmlCompositionDescription = Pick<HtmlAssetDescription, 'parts' | 'texts' | 'status'>;

/**
 * The copy an element renders as its own part: everything it contains except the copy of nested
 * labeled parts, which report themselves. Inline pieces stay in — a headline split into word spans,
 * or a line with one emphasized word, is a single part whose copy is the whole line.
 */
function ownText(element: Element): string {
    const clone = element.cloneNode(true) as Element;
    for (const nested of Array.from(clone.querySelectorAll('[data-codeo-asset]'))) {
        nested.remove();
    }
    return (clone.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Describe a composition document as what a video-level agent needs to reason about it: which
 * addressable parts exist and what copy they show. Both come from the `data-codeo-asset` labels the
 * design agent puts on every meaningful unit — a labeled card contributes its label, a labeled text
 * block contributes its label and its copy. A labeled wrapper doesn't restate the copy of labeled
 * parts nested inside it. The full HTML is deliberately never serialized into video state — it's
 * large, and authoring/editing it is the design subagent's job, not the video-level agent's.
 */
export function describeHtmlComposition(html: string): HtmlCompositionDescription {
    if (!html) {
        return { status: 'empty (awaiting generation)' };
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const labeled = Array.from(doc.querySelectorAll('[data-codeo-asset]')).filter((element) =>
        Boolean(element.getAttribute('data-codeo-asset')),
    );
    const parts = labeled.map((element) => element.getAttribute('data-codeo-asset') ?? '');
    const texts = labeled.map(ownText).filter(Boolean);
    return {
        ...(parts.length ? { parts: parts.join(', ') } : {}),
        ...(texts.length ? { texts: texts.join(' | ') } : {}),
    };
}
